import moment from "moment";

// Human-readable dates in the DVSA submission status line (e.g. "15 Jun 2026").
const displayDateFormat = "DD MMM YYYY";

// Builds the DVSA submission status line shown beneath the KPI period selector, from the dashboard
// response's dvsaSubmission block. The block's `state` drives which message (and which sub-object) is
// used; non-applicable sub-objects arrive as null, so each state guards the fields it reads and returns
// null rather than rendering a partial line. `streamLabel` names the KPI stream in the NotSubmitting
// copy ("Drivers" on the driver tab, "Maintenance" on the maintenance tab).
export function buildDvsaSubmissionMessage(dvsaSubmission, streamLabel) {
    if (dvsaSubmission == null) {
        return null;
    }

    if (dvsaSubmission.state === "Active") {
        const reportingPeriod = dvsaSubmission.reportingPeriod;
        // Active carries both the reporting period and the send date; without either there is no
        // meaningful line to show.
        if (reportingPeriod == null || dvsaSubmission.sendOnOrAround == null) {
            return null;
        }

        const startDate = moment(reportingPeriod.startDate).format(displayDateFormat);
        const endDate = moment(reportingPeriod.endDate).format(displayDateFormat);
        const periodText = `KPI results for Period ${reportingPeriod.isoPeriod} (${startDate} – ${endDate})`;

        // The send falls at the start of the active period, so it is in the past for most of that period.
        // A recorded sent date is the only reliable signal that it has happened, so the tense follows it.
        if (dvsaSubmission.sentDate != null) {
            const sentDate = moment(dvsaSubmission.sentDate).format(displayDateFormat);
            return `${periodText} were sent to the DVSA on ${sentDate}.`;
        }

        const sendDate = moment(dvsaSubmission.sendOnOrAround).format(displayDateFormat);

        return `${periodText} will be sent to the DVSA on or around ${sendDate}.`;
    }

    if (dvsaSubmission.state === "NotSubmitting") {
        return `This account is monitoring ${streamLabel} KPIs but is not currently submitting to the DVSA.`;
    }

    if (dvsaSubmission.state === "Pending") {
        // Pending carries the period submissions start from.
        if (dvsaSubmission.fromPeriod == null) {
            return null;
        }

        const fromPeriodText = `DVSA submissions begin from Period ${dvsaSubmission.fromPeriod.isoPeriod}`;

        // Without a parseable scheme start date the line stands on the period alone rather than naming
        // a date it does not have — the same guard the alert-rules modal applies, so the two agree.
        const schemeStart = dvsaSubmission.schemeStartDate == null ? null : moment(dvsaSubmission.schemeStartDate);
        if (schemeStart == null || !schemeStart.isValid()) {
            return `${fromPeriodText}.`;
        }

        return `This account joined the DVSA scheme on ${schemeStart.format(displayDateFormat)}, so ${fromPeriodText}.`;
    }

    return null;
}
